// Confirmation + progress modal for importing a trip export file as a new
// trip. The file is already parsed/validated by the time this opens (a bad
// file never gets here -- the page shows that error in its StatusMessage).
// Mirrors the import half of the logbook AddLogbookModal: confirm view,
// then a progress bar while legs are created one at a time, with errors
// inline because the page's status bar is hidden behind the modal.

import { useState } from 'react';
import Button from 'react-bootstrap/Button';
import Modal from 'react-bootstrap/Modal';
import ProgressBar from 'react-bootstrap/ProgressBar';
import { extractErrorText } from '../../fleet-shared/Common.mjs';

const legWord = (n) => (n === 1 ? 'leg' : 'legs');
const placeWord = (n) => (n === 1 ? 'place' : 'places');

// An import runs in two phases and the bar has to say which: the gazetteer
// rows the file's routes refer to are created first (so the legs can be bound
// to them), then the legs themselves. The places phase doesn't fire at all
// when this environment already has every name.
const PHASE_COPY = {
  places: ({ done, total }) => `Added ${done} of ${total} ${placeWord(total)}...`,
  legs: ({ done, total }) => `Imported ${done} of ${total} ${legWord(total)}...`,
};

// `parsed` is the validated { trip: { name }, legs } payload (null hides the
// modal). `onImport(parsed, onProgress)` performs the import; on success the
// parent navigates away and this modal unmounts.
const ImportTripModal = ({ parsed, onHide, onImport }) => {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [progress, setProgress] = useState(null);

  async function confirmImport() {
    setBusy(true);
    setError(null);
    setProgress({ done: 0, total: parsed.legs.length, phase: 'legs' });
    try {
      await onImport(parsed, (done, total, phase = 'legs') => setProgress({ done, total, phase }));
      // The parent closes the modal on success, but this component stays
      // mounted (only `show` flips) -- reset for the next open.
      setProgress(null);
      setBusy(false);
    } catch (err) {
      setError(extractErrorText(err));
      setProgress(null);
      setBusy(false);
    }
  }

  function handleHide() {
    if (busy) return;
    setError(null);
    setProgress(null);
    onHide();
  }

  const legCount = parsed?.legs.length ?? 0;
  const name = parsed?.trip.name?.trim();

  return (
    <Modal show={Boolean(parsed)} onHide={handleHide}>
      <Modal.Header closeButton>
        <Modal.Title>Import Trip</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {progress ? (
          <>
            <ProgressBar
              now={progress.total ? (progress.done / progress.total) * 100 : 100}
              animated
            />
            <p className="text-center mb-0 mt-2" role="status" aria-live="polite">
              {(PHASE_COPY[progress.phase] ?? PHASE_COPY.legs)(progress)}
            </p>
          </>
        ) : (
          <p className="mb-0">
            {name
              ? `Import ${legCount} ${legWord(legCount)} as a new trip "${name}"?`
              : `Import ${legCount} ${legWord(legCount)} as a new trip?`}
          </p>
        )}
        {error && (
          <p className="text-danger small mb-0 mt-3" role="alert">{error}</p>
        )}
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={handleHide} disabled={busy}>Cancel</Button>
        <Button variant="success" onClick={confirmImport} disabled={busy}>Import</Button>
      </Modal.Footer>
    </Modal>
  );
};

export default ImportTripModal;
